import { useEffect, useState } from "react";

import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { DragOverlay } from "@dnd-kit/core";
import { faUserMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import invariant from "ts-invariant";

import type { UserCourseLesson } from "api/schemas/UserCourse";
import type { ExerciseListItem } from "api/services/content/schemas/exercises";
import {
  usePostPatientTreatmentStart,
  usePostTreatmentSchedule,
  usePutPatientTreatmentSchedule,
  usePutTreatmentSchedule,
} from "api/services/treatment/hooks";
import {
  LibraryTreatmentSchedulePayloadSchema,
  TreatmentSchedulePayloadSchema,
} from "api/services/treatment/schemas/Treatment";
import type {
  DurationWeeks,
  Group,
  HealthFormType,
  Key,
  PostTreatmentSchedulePayload,
} from "api/services/treatment/schemas/Treatment";
import { useApiClient } from "api/useApiClient";
import { useProfileContext } from "contexts/ProfileContext";
import { useSnackbarContext } from "contexts/SnackbarContext/useSnackbarContext";
import { translateModifyPath } from "routes/library/views/Library";
import { BaseButton } from "shared/atoms/BaseButton";
import { ConfirmationPopup } from "shared/atoms/ConfirmationPopup";
import { CDndContext } from "shared/atoms/DragAndDrop";
import { Notification } from "shared/atoms/Notification";
import { type Breadcrumb, createBreadCrumb } from "shared/containers/View/components/ViewBreadcrumbs";
import { reportError } from "utils/errorReporting";
import { useNavigationWarning, useWindowSize } from "utils/hooks";
import { translateTemplateKey } from "utils/translations/translateTemplateKey";

import { ActivityCard } from "./ActivityCard";
import { ActivityLibrarySidebar } from "./ActivityLibrarySidebar";
import { ActivityLibraryItem } from "./ActivityLibrarySidebar/ActivityLibraryItem";
import type { ActiveTab } from "./ActivityLibrarySidebar/ActivityLibrarySidebar";
import { customCollisionDetection } from "./customCollisionDetection";
import { useEditorHistory, useTreatmentScheduleLoader } from "./hooks";
import type { PreviewActivity } from "./Modals";
import { ActivityPreviewModal, CopyExerciseModal, PinExerciseModal, TemplateChangeConfirmModal } from "./Modals";
import { PatientInformation } from "./PatientInformation";
import { type ExercisesViewMode, ProgramHeader } from "./ProgramHeader";
import { SettingsSidebar } from "./SettingsSidebar";
import { TreatmentEditorSkeleton } from "./Skeleton";
import { Container, DragPreviewCard, LoadMoreWeeksButton, ProgramContainer, StyledView } from "./styles";
import { TreatmentEditorHeader } from "./TreatmentEditorHeader";
import type { FormErrorSections, PanelTab } from "./TreatmentEditorPanel";
import { TreatmentEditorPanel } from "./TreatmentEditorPanel";
import { TreatmentScheduleExercises } from "./TreatmentScheduleExercises";
import { TreatmentScheduleLessons } from "./TreatmentScheduleLessons";
import type {
  HandleAddActivity,
  HandleApplySetsAndRepsToAll,
  HandleLevelChange,
  HandleSetsAndRepsChange,
  HandleWeekRangeChange,
  Patient,
  ShowRemoveConfirm,
  TimelineActivity,
  TimelineWeek,
  TreatmentEditorSettings,
} from "./types";
import {
  convertExerciseToTimelineActivity,
  convertWeeksToExercises,
  convertWeeksToScheduleExercises,
  createPayload,
  getDefaultValues,
  healthFormMatchesTreatment,
} from "./utils";
import { WeekTimeline } from "./WeekTimeline";

interface TreatmentEditorProps {
  editorSettings: TreatmentEditorSettings;
  patient?: Patient;
  treatmentScheduleID?: number;
}

export const TreatmentEditor: React.FC<TreatmentEditorProps> = ({ editorSettings, patient, treatmentScheduleID }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { profile } = useProfileContext();
  const { showSnackbar } = useSnackbarContext();
  const { isSmallScreen } = useWindowSize();
  invariant(profile);

  const { context, mode } = editorSettings;

  const [activeTab, setActiveTab] = useState<ActiveTab>("exercises");
  const [exercisesViewMode, setExercisesViewMode] = useState<ExercisesViewMode>("activities");
  const [lastAddedExerciseId, setLastAddedExerciseId] = useState<number | null>(null);
  const [activeActivity, setActiveActivity] = useState<TimelineActivity | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [activeDragExercise, setActiveDragExercise] = useState<any>(null);
  const [copiedActivities, setCopiedActivities] = useState<TimelineActivity[]>([]);
  const [exerciseLocks, setExerciseLocks] = useState<Map<number, { level: number }>>(new Map());

  const [isDraggingFromLibrary, setIsDraggingFromLibrary] = useState(false);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [pendingTemplateId, setPendingTemplateId] = useState<number | null>(null);
  const [pendingTemplateName, setPendingTemplateName] = useState<string>("");
  const [selectedActivityToPin, setSelectedActivityToPin] = useState<TimelineActivity | null>(null);
  const [selectedActivities, setSelectedActivities] = useState<Set<string>>(new Set());
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showChangeTemplateConfirm, setShowChangeTemplateConfirm] = useState(false);
  const [showEditTreatmentConfirm, setShowEditTreatmentConfirm] = useState(false);
  const [showProtocolTemplateWarning, setShowProtocolTemplateWarning] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<ShowRemoveConfirm>({
    activityId: undefined,
    exercise_id: undefined,
    open: false,
    weekNumber: undefined,
  });
  const [weeks, setWeeks] = useState<TimelineWeek[]>([]);
  const [weekWarnings, setWeekWarnings] = useState<Map<number, string>>(new Map());
  const [lessons, setLessons] = useState<UserCourseLesson[]>([]);
  const [previewActivity, setPreviewActivity] = useState<PreviewActivity | null>(null);

  const [panelTab, setPanelTab] = useState<PanelTab>("library");
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const { canUndo, hasUnsavedChanges, handleUndo, resetHistory, saveToHistory, setHasUnsavedChanges } =
    useEditorHistory({ setWeeks });

  const putPatientTreatmentSchedule = usePutPatientTreatmentSchedule();
  const postPatientTreatmentStart = usePostPatientTreatmentStart();
  const postTreatmentSchedule = usePostTreatmentSchedule();
  const putTreatmentSchedule = usePutTreatmentSchedule();

  useNavigationWarning({ warn: hasUnsavedChanges });

  const isLoading =
    putPatientTreatmentSchedule.isLoading ||
    postPatientTreatmentStart.isLoading ||
    postTreatmentSchedule.isLoading ||
    putTreatmentSchedule.isLoading;

  const form = useForm<PostTreatmentSchedulePayload>({
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: getDefaultValues(),
    resolver: zodResolver(
      context === "library" ? LibraryTreatmentSchedulePayloadSchema : TreatmentSchedulePayloadSchema
    ),
  });
  const {
    formState: { errors },
  } = form;

  const durationWeeks: DurationWeeks = form.watch("duration_weeks");
  const setDurationWeeks = (duration_weeks: DurationWeeks) => {
    form.setValue("duration_weeks", duration_weeks);
  };
  const exercisesPerDay: number = form.watch("exercises_per_day");
  const group: Group | null = form.watch("group");
  const healthFormType: HealthFormType = form.watch("health_form_type");
  const includesFunctionalTest: boolean = form.watch("includes_functional_test");
  const key: Key | null = form.watch("key");
  const name: string | null = form.watch("name");

  const healthFormMismatch = !healthFormMatchesTreatment({
    healthFormType,
    patientAilment: patient?.ailment,
    patientPrimaryJoint: patient?.location,
  });

  // Derived from the reactive form errors so the panel tab indicators clear as soon as a
  // value is fixed. Gated on hasAttemptedSave so tabs stay clean until the first save attempt.
  const formErrorSections: FormErrorSections = {
    library: hasAttemptedSave && Boolean(errors.exercises),
    settings:
      hasAttemptedSave &&
      Boolean(
        errors.name ||
          errors.group ||
          errors.exercises_per_day ||
          errors.includes_functional_test ||
          errors.health_form_type
      ),
  };

  const { isFetchingSchedule, loadTreatmentSchedule } = useTreatmentScheduleLoader({
    editorSettings,
    form,
    patient,
    treatmentScheduleID,
    resetHistory,
    setLessons,
    setWeeks,
  });

  // Reset pins when exercises per day changes and exceeds the new limit
  useEffect(() => {
    if (weeks.length === 0) return;

    // Count current pinned exercises
    const pinnedExerciseIDs = new Set<number>();
    weeks.forEach(week => {
      week.activities.forEach(activity => {
        if (activity.pinned && activity.type === "exercise") {
          pinnedExerciseIDs.add(activity.exercise_id);
        }
      });
    });

    // If we have more pinned exercises than the new limit, unpin all
    if (pinnedExerciseIDs.size > exercisesPerDay) {
      saveToHistory(weeks);
      setWeeks(prev =>
        prev.map(week => ({
          ...week,
          activities: week.activities.map(a => ({
            ...a,
            pinned: false,
          })),
        }))
      );

      // Show warning to user
      showWeekWarning(
        1,
        `Pins reset: Exercises per day reduced to ${exercisesPerDay}. Please re-pin up to ${exercisesPerDay} exercises.`
      );
    }
  }, [exercisesPerDay]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Meta" || e.key === "Control") {
        setIsMultiSelectMode(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Meta" || e.key === "Control") {
        setIsMultiSelectMode(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const onActivate = async () => {
    if (!patient?.id) return;

    const isValid = await validateForm();

    if (!isValid) {
      reportError(
        "TreatmentEditor: Failed to activate treatment due to validation errors",
        new Error(JSON.stringify(form.formState.errors))
      );
    } else {
      const payload = createPayload({
        duration_weeks: durationWeeks,
        weeks,
        exercises_per_day: exercisesPerDay,
        group,
        health_form_type: healthFormType,
        includes_functional_test: includesFunctionalTest,
        key,
        lessons,
        name,
      });

      try {
        await putPatientTreatmentSchedule.mutateAsync({ patientID: patient.id, payload });
        await postPatientTreatmentStart.mutateAsync(patient.id);
        showSnackbar({
          message: t("treatment.editor.feedback.treatment_activated"),
          severity: "success",
        });
        patient?.onRefreshPatient?.();
        onCancel();
      } catch (error) {
        showSnackbar({
          message: t("treatment.editor.feedback.errors.activate_treatment_failed"),
          severity: "error",
        });
        reportError("Failed to activate treatment", new Error(JSON.stringify({ error, variables: payload })));
      }
    }
  };

  const onCancel = () => {
    if (context === "patient") {
      navigate(`/patients/${patient?.id}/protocol/current`);
      return;
    }
    if (context === "library") {
      navigate("/library/your-library/templates");
      return;
    }

    navigate(-1);
  };

  const validateForm = async () => {
    form.setValue("exercises", convertWeeksToExercises(weeks));
    const isValid = await form.trigger();

    const { errors: formErrors } = form.formState;
    const hasLibraryErrors = Boolean(formErrors.exercises);
    const hasSettingsErrors = Boolean(
      formErrors.name ||
        formErrors.group ||
        formErrors.exercises_per_day ||
        formErrors.includes_functional_test ||
        formErrors.health_form_type ||
        healthFormMismatch
    );

    setHasAttemptedSave(true);

    if (hasLibraryErrors) {
      setPanelTab("library");
    } else if (hasSettingsErrors) {
      setPanelTab("settings");
    }

    return isValid;
  };

  const saveLibraryContext = async (payload: PostTreatmentSchedulePayload) => {
    if (mode === "build") {
      await postTreatmentSchedule.mutateAsync(payload);
      showSnackbar({
        message: t("treatment.editor.feedback.template_created"),
        severity: "success",
      });
      onCancel();
      return;
    }

    if (mode === "edit") {
      await putTreatmentSchedule.mutateAsync({
        treatmentScheduleID: Number(treatmentScheduleID),
        payload,
      });
      showSnackbar({
        message: t("treatment.editor.feedback.template_updated"),
        severity: "success",
      });
      onCancel();
    }
  };

  const savePatientContext = async () => {
    if (!patient?.id) return;

    const payload = createPayload({
      duration_weeks: durationWeeks,
      weeks,
      exercises_per_day: exercisesPerDay,
      group,
      health_form_type: healthFormType,
      includes_functional_test: includesFunctionalTest,
      key,
      lessons,
      name,
    });

    await putPatientTreatmentSchedule.mutateAsync({
      patientID: patient.id,
      payload,
    });
    showSnackbar({
      message: t("treatment.editor.feedback.treatment_updated"),
      severity: "success",
    });
    onCancel();
  };

  const onSaveDraft = async () => {
    if (!patient?.id) return;

    const isValid = await validateForm();

    if (!isValid) {
      reportError(
        "TreatmentEditor: Draft save failed due to validation errors",
        new Error(JSON.stringify(form.formState.errors))
      );
    } else {
      const payload = createPayload({
        duration_weeks: durationWeeks,
        weeks,
        exercises_per_day: exercisesPerDay,
        group,
        health_form_type: healthFormType,
        includes_functional_test: includesFunctionalTest,
        key,
        lessons,
        name,
      });

      try {
        await putPatientTreatmentSchedule.mutateAsync({ patientID: patient.id, payload });
        showSnackbar({
          message: t("treatment.editor.feedback.draft_saved"),
          severity: "success",
        });
        onCancel();
      } catch (error) {
        showSnackbar({
          message: t("treatment.editor.feedback.errors.draft_save_failed"),
          severity: "error",
        });
        reportError("TreatmentEditor: Draft save failed", new Error(JSON.stringify({ error, variables: payload })));
      }
    }
  };

  const onSave = async () => {
    const isValid = await validateForm();

    if (!isValid) {
      reportError("TreatmentEditor: Form validation failed on save", new Error(JSON.stringify(form.formState.errors)));
    } else {
      const payload = createPayload({
        duration_weeks: durationWeeks,
        weeks,
        exercises_per_day: exercisesPerDay,
        group,
        health_form_type: healthFormType,
        includes_functional_test: includesFunctionalTest,
        key,
        lessons,
        name,
      });

      if (context === "library") {
        saveLibraryContext(payload);
        return;
      }
      if (context === "patient") {
        if (mode === "edit") {
          setShowEditTreatmentConfirm(true);
        } else {
          savePatientContext();
        }
      }
    }
  };

  const showWeekWarning = (weekNumber: number, message: string) => {
    setWeekWarnings(prev => new Map(prev).set(weekNumber, message));
    setTimeout(() => {
      setWeekWarnings(prev => {
        const newMap = new Map(prev);
        newMap.delete(weekNumber);
        return newMap;
      });
    }, 3000);
  };

  const dismissWeekWarning = (weekNumber: number) => {
    setWeekWarnings(prev => {
      const newMap = new Map(prev);
      newMap.delete(weekNumber);
      return newMap;
    });
  };

  const loadMoreWeeks = () => {
    const currentWeekCount = weeks.length;

    // Find exercises that are "ongoing" (max week equals current week count)
    const ongoingExerciseIds = new Set<number>();
    const exerciseActivityMap = new Map<number, TimelineActivity>();

    weeks.forEach(week => {
      week.activities.forEach(activity => {
        if (activity.type === "exercise") {
          const exerciseId = activity.exercise_id;
          exerciseActivityMap.set(exerciseId, activity);
        }
      });
    });

    // Check which exercises have their max week equal to current week count
    exerciseActivityMap.forEach((_, exerciseId) => {
      const weeksWithExercise = weeks
        .filter(week => week.activities.some(a => a.exercise_id === exerciseId))
        .map(w => w.weekNumber);

      const maxWeek = Math.max(...weeksWithExercise);
      if (maxWeek === currentWeekCount) {
        ongoingExerciseIds.add(exerciseId);
      }
    });

    // Create additional weeks with ongoing exercises
    const additionalWeeks: TimelineWeek[] = Array.from({ length: 6 }, (_, index) => {
      const weekNumber = weeks.length + index + 1;
      const activities: TimelineActivity[] = [];

      // Add ongoing exercises to this new week
      ongoingExerciseIds.forEach(exerciseId => {
        const referenceActivity = exerciseActivityMap.get(exerciseId);
        if (referenceActivity) {
          activities.push({
            ...referenceActivity,
            id: `exercise-${exerciseId}-${weekNumber}-${Date.now()}-${Math.random()}`,
            weekNumber,
          });
        }
      });

      return {
        weekNumber,
        activities,
        expanded: false,
      };
    });

    const updatedWeeks = [...weeks, ...additionalWeeks];
    setWeeks(updatedWeeks);
    saveToHistory(updatedWeeks);
  };

  const onDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const sourceData = active.data.current;

    if (sourceData?.type === "activity_card") {
      const activity = sourceData.activity as TimelineActivity;
      setActiveActivity(activity);
      setIsDraggingFromLibrary(false);
    } else if (sourceData?.type === "library_item") {
      setActiveDragExercise({ ...sourceData.activity, type: sourceData.activityType });
      setIsDraggingFromLibrary(true);
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveActivity(null);
      setActiveDragExercise(null);
      return;
    }

    const sourceData = active.data.current;
    const targetData = over.data.current;

    if (sourceData?.activityType === "exercise") {
      if (sourceData?.type === "library_item") {
        if (targetData?.droppableType === "exercises") {
          const item = sourceData.activity;
          addExerciseToWeek(item, null);
        }

        if (targetData?.droppableType === "week") {
          const item = sourceData.activity;
          const targetWeek = targetData.weekNumber as number;
          addExerciseToWeek(item, targetWeek);
        }
      }

      if (sourceData?.type === "activity_card") {
        if (targetData?.droppableType === "week") {
          const activity = sourceData.activity as TimelineActivity;
          const sourceWeek = sourceData.weekNumber as number;
          const targetWeek = targetData.weekNumber as number;

          if (sourceWeek !== targetWeek) {
            moveExerciseBetweenWeeks(activity, sourceWeek, targetWeek);
          }
        }
      }
    }

    if (sourceData?.activityType === "lesson") {
      if (sourceData?.type === "library_item") {
        const item = sourceData.activity;
        addLessonToLessons(item);
      }

      if (sourceData?.type === "activity_card") {
        const draggedLesson = sourceData.activity as UserCourseLesson;

        if (!over) {
          return;
        }

        reorderLesson(draggedLesson, over.id);
      }
    }

    setActiveActivity(null);
    setActiveDragExercise(null);
    setIsDraggingFromLibrary(false);
  };

  const addExerciseToWeek = (item: ExerciseListItem, weekNumber: number | null) => {
    if (weekNumber === null) {
      const exerciseExists = weeks.some(week =>
        week.activities.some(activity => activity.exercise_id === item.exercise_id)
      );

      if (exerciseExists) {
        showWeekWarning(1, t("treatment.editor.warnings.exercise_already_added"));
        return;
      }

      saveToHistory(weeks);
      setLastAddedExerciseId(item.exercise_id);

      setWeeks(prev => {
        const newWeeks = prev.map(week => {
          const newActivity = convertExerciseToTimelineActivity({
            exercise: item,
            level_locked: false,
            pinned: false,
            recommended_level: 1,
            weekNumber: week.weekNumber,
          });

          return {
            ...week,
            activities: [...week.activities, newActivity],
          };
        });
        form.setValue("exercises", convertWeeksToScheduleExercises(newWeeks), { shouldValidate: true });
        return newWeeks;
      });
    } else {
      const weekData = weeks.find(week => week.weekNumber === weekNumber);
      const activityExists = weekData?.activities.some(activity => activity.exercise_id === item.exercise_id);

      if (activityExists) {
        showWeekWarning(weekNumber, t("treatment.editor.warnings.exercise_already_added"));
        return;
      }

      const isPinnedInAnyWeek = weeks.some(week =>
        week.activities.some(activity => activity.exercise_id === item.exercise_id && activity.pinned === true)
      );

      const lockedLevelData = weeks
        .filter(week => week.weekNumber < weekNumber)
        .flatMap(week => week.activities)
        .find(activity => activity.exercise_id === item.exercise_id && activity.level_locked === true);

      saveToHistory(weeks);

      const newActivity = convertExerciseToTimelineActivity({
        exercise: item,
        level_locked: !!lockedLevelData,
        pinned: isPinnedInAnyWeek,
        recommended_level: lockedLevelData ? lockedLevelData.level : 1,
        weekNumber,
      });

      setWeeks(prev => {
        const newWeeks = prev.map(week => {
          if (week.weekNumber === weekNumber) {
            return {
              ...week,
              activities: [...week.activities, newActivity],
            };
          }
          return week;
        });
        form.setValue("exercises", convertWeeksToScheduleExercises(newWeeks), { shouldValidate: true });
        return newWeeks;
      });
    }
  };

  const moveExerciseBetweenWeeks = (activity: TimelineActivity, sourceWeek: number, targetWeek: number) => {
    saveToHistory(weeks); // Save current state before change
    const updatedActivity = {
      ...activity,
      weekNumber: targetWeek,
    };

    setWeeks(prev =>
      prev.map(week => {
        if (week.weekNumber === sourceWeek) {
          return {
            ...week,
            activities: week.activities.filter(a => a.id !== activity.id),
          };
        }

        if (week.weekNumber === targetWeek) {
          const movedActivity = {
            ...updatedActivity,
            id: `${activity.exercise_id}-${targetWeek}-${Date.now()}`,
          };
          return {
            ...week,
            activities: [...week.activities, movedActivity],
          };
        }

        return week;
      })
    );
  };

  const syncLessons = (updatedLessons: UserCourseLesson[]) => {
    setLessons(updatedLessons);
    form.setValue(
      "lessons",
      updatedLessons.map((lesson, index) => ({
        position: index + 1,
        text_lesson_id: lesson.id,
      }))
    );
  };

  const addLessonToLessons = (item: UserCourseLesson) => {
    const activityExists = lessons.some(a => a.id === item.id);

    if (activityExists) {
      return;
    }

    syncLessons([...lessons, item]);
  };

  const reorderLesson = (draggedLesson: UserCourseLesson, overId: string | number) => {
    const draggedIndex = lessons.findIndex(l => l.id === draggedLesson.id);

    if (draggedIndex === -1) return;

    const overIndex = lessons.findIndex(l => l.id === overId);

    if (overIndex !== -1 && draggedIndex !== overIndex) {
      const newLessons = [...lessons];
      const [removed] = newLessons.splice(draggedIndex, 1);
      newLessons.splice(overIndex, 0, removed);
      syncLessons(newLessons);
    }
  };

  const handleAddActivity: HandleAddActivity = (activity, type) => {
    if (type === "exercise") {
      addExerciseToWeek(activity as ExerciseListItem, null);
    } else if (type === "lesson") {
      addLessonToLessons(activity as UserCourseLesson);
    }
  };

  const removeExercise = (exercise_id: number, weekNumber: number | null) => {
    saveToHistory(weeks);
    const newWeeks =
      weekNumber === null
        ? weeks.map(week => ({
            ...week,
            activities: week.activities.filter(a => a.exercise_id !== exercise_id),
          }))
        : weeks.map(week =>
            week.weekNumber === weekNumber
              ? { ...week, activities: week.activities.filter(a => a.exercise_id !== exercise_id) }
              : week
          );
    setWeeks(newWeeks);
    // Keep the form's exercises field in sync (mirrors the add handlers) so the library tab
    // indicator and the inline "no exercises" error stay correct after a removal.
    form.setValue("exercises", convertWeeksToScheduleExercises(newWeeks), { shouldValidate: hasAttemptedSave });
  };

  const removeLesson = (lessonID: number) => {
    syncLessons(lessons.filter(lesson => lesson.id !== lessonID));
  };

  const handleRemoveActivity = (activity: TimelineActivity, weekNumber: number) => {
    setShowRemoveConfirm({ exercise_id: activity.exercise_id, open: true, weekNumber });
  };

  const handleLevelChange: HandleLevelChange = ({ exercise_id, recommended_level }) => {
    saveToHistory(weeks);
    setWeeks(prev =>
      prev.map(week => {
        return {
          ...week,
          activities: week.activities.map(a =>
            a.exercise_id === exercise_id
              ? {
                  ...a,
                  image_url: a?.levels?.[recommended_level - 1]?.image_url || a.image_url,
                  level: recommended_level,
                  recommended_level,
                  title: a?.levels?.[recommended_level - 1]?.title || a.title,
                  variation_title: a?.levels?.[recommended_level - 1]?.variation_title,
                  video_thumbnail_file_url:
                    a?.levels?.[recommended_level - 1]?.video_thumbnail_file_url || a.video_thumbnail_file_url,
                }
              : a
          ),
        };
      })
    );
  };

  const handlePinExercise = ({
    activityId,
    exercise_id,
    weekNumber,
  }: {
    activityId?: string;
    exercise_id?: number;
    weekNumber?: number;
  }) => {
    saveToHistory(weeks);

    if (exercise_id) {
      const targetActivity = weeks.flatMap(week => week.activities).find(a => a.exercise_id === exercise_id);

      if (!targetActivity) return;
      setSelectedActivityToPin(targetActivity);
    } else {
      const targetWeek = weeks.find(w => w.weekNumber === weekNumber);
      const targetActivity = targetWeek?.activities.find(a => a.id === activityId);

      if (!targetActivity) return;
      setSelectedActivityToPin(targetActivity);
    }
  };

  const onPinExercise = (activity: TimelineActivity, days: number[]) => {
    saveToHistory(weeks);

    const { exercise_id } = activity;

    const activityWithDays: TimelineActivity = {
      ...activity,
      days,
      pinned: days.length > 0,
    };

    setWeeks(prevWeeks =>
      prevWeeks.map(week => {
        const exerciseExists = week.activities.some(a => a.exercise_id === exercise_id);

        const updatedActivities = week.activities.map(a =>
          a.exercise_id === exercise_id ? { ...a, days, pinned: days.length > 0 } : a
        );

        // If exercise doesn't exist and we're pinning (days.length > 0), add it
        if (!exerciseExists && days.length > 0) {
          const newActivity: TimelineActivity = {
            ...activityWithDays,
            id: `exercise-${activityWithDays.exercise_id}-${week.weekNumber}-${Date.now()}-${Math.random()}`,
            weekNumber: week.weekNumber,
          };
          return {
            ...week,
            activities: [...updatedActivities, newActivity],
          };
        }

        return {
          ...week,
          activities: updatedActivities,
        };
      })
    );
  };

  const handleExerciseSetsAndRepsChange: HandleSetsAndRepsChange = ({
    exercise_id,
    recommended_sets,
    recommended_reps,
  }) => {
    saveToHistory(weeks);
    setWeeks(prev =>
      prev.map(week => {
        return {
          ...week,
          activities: week.activities.map(a => {
            if (a.exercise_id === exercise_id) {
              return {
                ...a,
                ...(recommended_sets !== undefined && { recommended_sets }),
                ...(recommended_reps !== undefined && { recommended_reps }),
              };
            }
            return a;
          }),
        };
      })
    );
  };

  const handleApplySetsAndRepsToAll: HandleApplySetsAndRepsToAll = ({ recommended_reps, recommended_sets }) => {
    saveToHistory(weeks);
    setWeeks(prev =>
      prev.map(week => ({
        ...week,
        activities: week.activities.map(a =>
          a.duration_icon === "repetitions" ? { ...a, recommended_reps, recommended_sets } : a
        ),
      }))
    );
  };

  const toggleWeekExpanded = (weekNumber: number) => {
    setWeeks(prev => prev.map(week => (week.weekNumber === weekNumber ? { ...week, expanded: !week.expanded } : week)));
  };

  const handleDurationWeeksChange = (newLength: DurationWeeks) => {
    setDurationWeeks(newLength);

    const currentLength = weeks.length;
    const targetLength = newLength === null ? currentLength : newLength;

    if (targetLength > currentLength) {
      const additionalWeeks: TimelineWeek[] = Array.from({ length: targetLength - currentLength }, (_, index) => ({
        weekNumber: currentLength + index + 1,
        activities: [],
        expanded: false,
      }));
      const updatedWeeks = [...weeks, ...additionalWeeks];
      setWeeks(updatedWeeks);
      saveToHistory(updatedWeeks);
    } else if (targetLength < currentLength) {
      setWeeks(prev => prev.slice(0, targetLength));
    }
  };

  const handleLoadTemplateFromSidebar = async (templateID: number) => {
    try {
      const treatmentScheduleData = await apiClient.getTreatmentSchedule(templateID);
      const templateName = treatmentScheduleData.key
        ? translateTemplateKey(treatmentScheduleData.key)
        : treatmentScheduleData?.name || t("treatment.editor.selected_template");
      setPendingTemplateId(templateID);
      setPendingTemplateName(templateName);
      setShowProtocolTemplateWarning(true);
    } catch (error) {
      reportError("Failed to load template for confirmation", new Error(JSON.stringify({ error, templateID })));
    }
  };

  const confirmProtocolTemplateChange = async () => {
    setShowProtocolTemplateWarning(false);

    if (pendingTemplateId !== null) {
      await loadTreatmentSchedule({ templateID: pendingTemplateId });
      setPendingTemplateId(null);
      setPendingTemplateName("");
    }
  };

  const clearProgram = (content: "all" | "exercises" | "lessons") => {
    switch (content) {
      case "all":
        setWeeks(prev => prev.map(week => ({ ...week, activities: [] })));
        syncLessons([]);
        break;
      case "exercises":
        setWeeks(prev => prev.map(week => ({ ...week, activities: [] })));
        break;
      case "lessons":
        syncLessons([]);
        break;
      default:
        break;
    }
    setShowClearConfirm(false);
    setHasUnsavedChanges(true);
  };

  const handleActivityClick = (activity: TimelineActivity) => {
    if (isMultiSelectMode) {
      setSelectedActivities(prev => {
        const newSet = new Set(prev);
        if (newSet.has(activity.id)) {
          newSet.delete(activity.id);
        } else {
          newSet.add(activity.id);
        }
        return newSet;
      });
    }
  };

  const handleCopySelected = (weekNumber: number) => {
    const selectedExercises =
      weeks.find(w => w.weekNumber === weekNumber)?.activities.filter(a => selectedActivities.has(a.id)) || [];

    if (selectedExercises.length > 0) {
      setCopiedActivities(selectedExercises);
      setShowPasteModal(true);
    }
  };

  const handleCopyActivity = (activity: TimelineActivity) => {
    setCopiedActivities([activity]);
    setShowPasteModal(true);
  };

  const handlePasteActivity = (selectedWeeks: number[]) => {
    if (copiedActivities.length === 0) return;

    setWeeks(prevWeeks =>
      prevWeeks.map(week => {
        if (!selectedWeeks.includes(week.weekNumber)) return week;

        const newActivities: TimelineActivity[] = [];

        copiedActivities.forEach(copiedActivity => {
          const exerciseExists = week.activities.some(a => a.exercise_id === copiedActivity.exercise_id);
          if (!exerciseExists) {
            const newActivity: TimelineActivity = {
              ...copiedActivity,
              id: `exercise-${copiedActivity.exercise_id}-${week.weekNumber}-${Date.now()}-${Math.random()}`,
              weekNumber: week.weekNumber,
            };
            newActivities.push(newActivity);
          }
        });

        return { ...week, activities: [...week.activities, ...newActivities] };
      })
    );

    setCopiedActivities([]);
    setSelectedActivities(new Set());
  };

  const handleToggleLevelLock: HandleLevelChange = ({ exercise_id, recommended_level }) => {
    setExerciseLocks(prev => {
      const newLocks = new Map(prev);
      const existingLock = newLocks.get(exercise_id);

      if (existingLock && existingLock.level === recommended_level) {
        newLocks.delete(exercise_id);
      } else {
        newLocks.set(exercise_id, { level: recommended_level });
      }

      return newLocks;
    });

    setWeeks(prevWeeks =>
      prevWeeks.map(week => ({
        ...week,
        activities: week.activities.map(activity => {
          if (activity.exercise_id === exercise_id) {
            const lock = exerciseLocks.get(exercise_id);
            const isLocking = !lock || lock.level !== recommended_level;

            return {
              ...activity,
              level_locked: isLocking,
              level: isLocking ? recommended_level : activity.level,
            };
          }
          return activity;
        }),
      }))
    );

    setHasUnsavedChanges(true);
  };

  const handleWeekRangeChange: HandleWeekRangeChange = ({ exercise_id, targetWeek, type }) => {
    // Find the current weeks where this exercise exists
    const weeksWithExercise = weeks
      .filter(week => week.activities.some(a => a.exercise_id === exercise_id))
      .map(w => w.weekNumber);

    if (weeksWithExercise.length === 0) return;

    const currentStartWeek = Math.min(...weeksWithExercise);
    const currentEndWeek = Math.max(...weeksWithExercise);
    const currentBoundaryWeek = type === "start" ? currentStartWeek : currentEndWeek;

    // No change needed
    if (targetWeek === currentBoundaryWeek) return;

    saveToHistory(weeks);

    // Get a reference activity to copy properties from
    const referenceActivity = weeks.flatMap(w => w.activities).find(a => a.exercise_id === exercise_id);
    if (!referenceActivity) return;

    const isExpanding =
      type === "start"
        ? targetWeek < currentBoundaryWeek // start: moving backward expands
        : targetWeek > currentBoundaryWeek; // end: moving forward expands

    setWeeks(prev =>
      prev.map(week => {
        let shouldAddExercise = false;
        let shouldRemoveExercise = false;

        if (type === "start") {
          // For start week changes
          if (isExpanding) {
            // Expanding: add weeks from targetWeek up to (but not including) currentStartWeek
            shouldAddExercise = week.weekNumber >= targetWeek && week.weekNumber < currentStartWeek;
          } else {
            // Contracting: remove weeks before targetWeek, but ensure targetWeek is included
            shouldRemoveExercise = week.weekNumber < targetWeek;
            shouldAddExercise = week.weekNumber === targetWeek;
          }
        } else if (isExpanding) {
          // For end week changes - Expanding: add weeks from (but not including) currentEndWeek up to targetWeek
          shouldAddExercise = week.weekNumber <= targetWeek && week.weekNumber > currentEndWeek;
        } else {
          // For end week changes - Contracting: remove weeks after targetWeek, but ensure targetWeek is included
          shouldRemoveExercise = week.weekNumber > targetWeek;
          shouldAddExercise = week.weekNumber === targetWeek;
        }

        // Add exercise to week
        if (shouldAddExercise) {
          const exerciseExists = week.activities.some(a => a.exercise_id === exercise_id);
          if (exerciseExists) return week;

          const newActivity: TimelineActivity = {
            ...referenceActivity,
            id: `exercise-${exercise_id}-${week.weekNumber}-${Date.now()}-${Math.random()}`,
            weekNumber: week.weekNumber,
          };

          return {
            ...week,
            activities: [...week.activities, newActivity],
          };
        }

        // Remove exercise from week
        if (shouldRemoveExercise) {
          return {
            ...week,
            activities: week.activities.filter(a => a.exercise_id !== exercise_id),
          };
        }

        return week;
      })
    );
  };

  const createAdditionalBreadcrumbs = () => {
    const additionalBreadcrumbs: Breadcrumb[] = [];

    if (context === "library") {
      additionalBreadcrumbs.push(createBreadCrumb(t("common.templates"), "/library/your-library/templates"));
      if (name) {
        additionalBreadcrumbs.push(createBreadCrumb(name || "", ""));
      }
    }

    if (context === "patient") {
      additionalBreadcrumbs.push(createBreadCrumb(patient?.name || "", `/patients/${patient?.id}/protocol/current`));
      additionalBreadcrumbs.push(createBreadCrumb(t("treatment.editor.title"), ""));
    }

    if (mode === "build") {
      additionalBreadcrumbs.push(createBreadCrumb(translateModifyPath("new"), ""));
    } else if (mode === "edit") {
      additionalBreadcrumbs.push(createBreadCrumb(translateModifyPath("edit"), ""));
    }

    return additionalBreadcrumbs;
  };

  return (
    <StyledView
      title={t("treatment.editor.title")}
      info={
        patient
          ? {
              content: <PatientInformation patient={patient} />,
              icon: faUserMagnifyingGlass,
            }
          : undefined
      }
      additionalBreadcrumbs={createAdditionalBreadcrumbs()}
      actionButtons={[
        <TreatmentEditorHeader
          mode={mode}
          canUndo={canUndo}
          hasErrors={healthFormMismatch}
          isLoading={isLoading}
          onActivate={context === "patient" && mode === "build" ? onActivate : undefined}
          onReset={() => setShowClearConfirm(true)}
          onSave={onSave}
          onSaveDraft={context === "patient" && mode === "build" ? onSaveDraft : undefined}
          onUndo={handleUndo}
        />,
      ]}
    >
      <CDndContext collisionDetection={customCollisionDetection} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <Container>
          {isSmallScreen ? (
            <Box height="100%" padding={4}>
              {t("treatment.editor.feedback.screen_too_small")}
            </Box>
          ) : (
            <FormProvider {...form}>
              <ProgramContainer>
                {isFetchingSchedule ? (
                  <TreatmentEditorSkeleton />
                ) : (
                  <>
                    <ProgramHeader
                      activeTab={activeTab}
                      activitiesLength={
                        activeTab === "exercises" ? convertWeeksToScheduleExercises(weeks).length : lessons.length
                      }
                      exercisesViewMode={exercisesViewMode}
                      onCollapseWeeks={() => setWeeks(prev => prev.map(week => ({ ...week, expanded: false })))}
                      onExpandWeeks={() => setWeeks(prev => prev.map(week => ({ ...week, expanded: true })))}
                      setExercisesViewMode={setExercisesViewMode}
                    />

                    {activeTab === "exercises" && (
                      <>
                        {exercisesViewMode === "activities" && (
                          <TreatmentScheduleExercises
                            exercises={convertWeeksToScheduleExercises(weeks)}
                            exerciseProps={{
                              durationWeeks: durationWeeks || weeks.length,
                              exercisesPerDay,
                              pinnedExercisesCount: (() => {
                                // TODO: cleanup
                                // Count unique pinned exercises across all weeks
                                const pinnedExerciseIds = new Set<number>();
                                weeks.forEach(week => {
                                  week.activities.forEach(activity => {
                                    if (activity.pinned) {
                                      pinnedExerciseIds.add(activity.exercise_id);
                                    }
                                  });
                                });
                                return pinnedExerciseIds.size;
                              })(),
                              onLevelChange: handleLevelChange,
                              onPin: handlePinExercise,
                              onSetsAndRepsChange: handleExerciseSetsAndRepsChange,
                              onToggleLevelLock: handleToggleLevelLock,
                              onWeekRangeChange: handleWeekRangeChange,
                            }}
                            noExercisesError={
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              errors?.exercises?.type === "too_small"
                            }
                            onPreview={(id, part) => setPreviewActivity({ id, part, type: "exercise" })}
                            onRemove={exerciseId => removeExercise(exerciseId, null)}
                            lastAddedExerciseId={lastAddedExerciseId}
                            onScrolled={() => setLastAddedExerciseId(null)}
                          />
                        )}

                        {exercisesViewMode === "weeks" && (
                          <>
                            <WeekTimeline
                              currentWeek={patient?.currentWeek as number}
                              editorSettings={editorSettings}
                              exercisesPerDay={exercisesPerDay}
                              isDraggingFromLibrary={isDraggingFromLibrary}
                              isMultiSelectMode={isMultiSelectMode}
                              noExercisesError={
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                errors?.exercises?.type === "too_small"
                              }
                              selectedActivities={selectedActivities}
                              totalPinnedCount={(() => {
                                // TODO: cleanup
                                // Count unique pinned exercises across all weeks
                                const pinnedExerciseIds = new Set<number>();
                                weeks.forEach(week => {
                                  week.activities.forEach(activity => {
                                    if (activity.pinned) {
                                      pinnedExerciseIds.add(activity.exercise_id);
                                    }
                                  });
                                });
                                return pinnedExerciseIds.size;
                              })()}
                              weeks={weeks}
                              weekWarnings={weekWarnings}
                              onActivityClick={handleActivityClick}
                              onCopyActivity={handleCopyActivity}
                              onCopySelected={handleCopySelected}
                              onDismissWarning={dismissWeekWarning}
                              onLevelChange={handleLevelChange}
                              onPinExercise={handlePinExercise}
                              onPreview={(id, part) => setPreviewActivity({ id, part, type: "exercise" })}
                              onRemoveActivity={handleRemoveActivity}
                              onToggleExpanded={toggleWeekExpanded}
                              onToggleLevelLock={handleToggleLevelLock}
                              onSetsAndRepsChange={handleExerciseSetsAndRepsChange}
                            />

                            {/* Load More Weeks button - show if ongoing mode */}
                            {durationWeeks === null && (
                              <Stack spacing={2}>
                                <Notification>{t("treatment.editor.ongoing_mode_explanation")}</Notification>

                                <LoadMoreWeeksButton onClick={loadMoreWeeks}>
                                  {t("treatment.editor.add_more_weeks", {
                                    startWeek: weeks.length + 1,
                                    endWeek: weeks.length + 6,
                                  })}
                                </LoadMoreWeeksButton>
                              </Stack>
                            )}
                          </>
                        )}
                      </>
                    )}

                    {activeTab === "lessons" && (
                      <TreatmentScheduleLessons
                        lessons={lessons}
                        onPreview={id => setPreviewActivity({ id, part: 1, type: "lesson" })}
                        onRemove={removeLesson}
                      />
                    )}
                  </>
                )}
              </ProgramContainer>

              <TreatmentEditorPanel
                formErrorSections={{
                  library: formErrorSections.library,
                  settings: formErrorSections.settings || healthFormMismatch,
                }}
                panelTab={panelTab}
                setPanelTab={setPanelTab}
              >
                <>
                  {panelTab === "library" && (
                    <ActivityLibrarySidebar
                      activeTab={activeTab}
                      addedExercises={convertWeeksToScheduleExercises(weeks)}
                      addedLessons={lessons}
                      onAddActivity={handleAddActivity}
                      onRemoveActivity={(id, type) => {
                        if (type === "exercise") {
                          removeExercise(id, null);
                        } else {
                          removeLesson(id);
                        }
                      }}
                      setActiveTab={setActiveTab}
                    />
                  )}

                  {panelTab === "settings" && (
                    <SettingsSidebar
                      durationWeeks={durationWeeks}
                      editorSettings={editorSettings}
                      formErrors={errors}
                      onApplySetsAndRepsToAll={handleApplySetsAndRepsToAll}
                      onDurationWeeksChange={handleDurationWeeksChange}
                      onLoadTemplate={handleLoadTemplateFromSidebar}
                      patientPrimaryJoint={patient?.location}
                      patientAilment={patient?.ailment}
                      healthFormMismatch={healthFormMismatch}
                    />
                  )}
                </>
              </TreatmentEditorPanel>
            </FormProvider>
          )}
        </Container>

        <DragOverlay dropAnimation={null}>
          {activeActivity && (
            <DragPreviewCard>
              <ActivityCard activity={activeActivity} isDragging weekNumber={activeActivity.weekNumber} />
            </DragPreviewCard>
          )}
          {activeDragExercise && (
            <ActivityLibraryItem activity={activeDragExercise} type={activeDragExercise.type} hideControls />
          )}
        </DragOverlay>
      </CDndContext>

      {selectedActivityToPin && (
        <PinExerciseModal
          selectedActivityToPin={selectedActivityToPin}
          onClose={() => {
            setSelectedActivityToPin(null);
          }}
          onPin={onPinExercise}
        />
      )}

      {showPasteModal && copiedActivities.length > 0 && (
        <CopyExerciseModal
          currentWeek={patient?.currentWeek as number}
          durationWeeks={durationWeeks}
          exercises={copiedActivities}
          weeks={weeks.length}
          onClose={() => {
            setShowPasteModal(false);
            setCopiedActivities([]);
          }}
          onCopy={handlePasteActivity}
        />
      )}

      <ConfirmationPopup
        isOpen={showClearConfirm}
        title={t("treatment.editor.clear_program.title")}
        body={t("treatment.editor.clear_program.body")}
        content={
          <Stack spacing={2}>
            <BaseButton
              text={t("treatment.editor.clear_program.clear_exercises")}
              onClick={() => {
                clearProgram("exercises");
              }}
              variant="danger"
            />
            <BaseButton
              text={t("treatment.editor.clear_program.clear_lessons")}
              onClick={() => {
                clearProgram("lessons");
              }}
              variant="danger"
            />
            <BaseButton
              text={t("treatment.editor.clear_program.clear_all")}
              onClick={() => {
                clearProgram("all");
              }}
              variant="danger"
            />
            <BaseButton
              text={t("buttons.cancel")}
              onClick={() => setShowClearConfirm(false)}
              variant="tertiaryBlue"
              uppercase
            />
          </Stack>
        }
        onClickOutside={() => setShowClearConfirm(false)}
      />

      <ConfirmationPopup
        isOpen={showRemoveConfirm.open}
        title={t("treatment.editor.confirmations.remove.title")}
        body={t("treatment.editor.confirmations.remove.body")}
        content={
          <Stack spacing={2}>
            <BaseButton
              text={t("treatment.editor.confirmations.remove.remove_from_this_week")}
              onClick={() => {
                removeExercise(showRemoveConfirm.exercise_id as number, showRemoveConfirm.weekNumber as number);
                setShowRemoveConfirm({
                  activityId: undefined,
                  exercise_id: undefined,
                  open: false,
                  weekNumber: undefined,
                });
              }}
              variant="danger"
            />
            <BaseButton
              text={t("treatment.editor.confirmations.remove.remove_from_all_weeks")}
              onClick={() => {
                removeExercise(showRemoveConfirm.exercise_id as number, null);
                setShowRemoveConfirm({
                  activityId: undefined,
                  exercise_id: undefined,
                  open: false,
                  weekNumber: undefined,
                });
              }}
              variant="danger"
            />
            <BaseButton
              text={t("buttons.cancel")}
              onClick={() =>
                setShowRemoveConfirm({
                  activityId: undefined,
                  exercise_id: undefined,
                  open: false,
                  weekNumber: undefined,
                })
              }
              variant="tertiaryBlue"
              uppercase
            />
          </Stack>
        }
        onClickOutside={() => setShowRemoveConfirm({ open: false, weekNumber: undefined })}
      />

      <ConfirmationPopup
        isOpen={showChangeTemplateConfirm}
        title={t("treatment.editor.confirmations.change_template.title")}
        body={t("treatment.editor.confirmations.change_template.body")}
        onConfirm={() => setShowChangeTemplateConfirm(false)}
        onCancel={() => setShowChangeTemplateConfirm(false)}
        onClickOutside={() => setShowChangeTemplateConfirm(false)}
      />

      <ConfirmationPopup
        isOpen={showEditTreatmentConfirm}
        title={t("treatment.editor.confirmations.edit_treatment.title")}
        body={t("treatment.editor.confirmations.edit_treatment.body")}
        onConfirm={savePatientContext}
        onCancel={() => setShowEditTreatmentConfirm(false)}
        onClickOutside={() => setShowEditTreatmentConfirm(false)}
      />

      <TemplateChangeConfirmModal
        isOpen={showProtocolTemplateWarning}
        templateName={pendingTemplateName}
        onConfirm={confirmProtocolTemplateChange}
        onCancel={() => {
          setShowProtocolTemplateWarning(false);
          setPendingTemplateId(null);
          setPendingTemplateName("");
        }}
      />

      {previewActivity && (
        <ActivityPreviewModal
          activity={previewActivity}
          onClose={() => {
            setPreviewActivity(null);
          }}
        />
      )}
    </StyledView>
  );
};
